import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  copyNodes, createFolder, deleteNodes, downloadUrl, moveNodes, zipUrl,
} from '../api/files.ts';
import { restoreTrash } from '../api/trash.ts';
import { baseName, joinPath, parentPath } from '../lib/format.ts';
import { toast, useToasts } from '../store/toast.ts';
import { useSelection } from '../store/selection.ts';

export function useFolderActions(namespaceId: string) {
  const queryClient = useQueryClient();
  const clearSelection = useSelection((s) => s.clear);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['nodes', namespaceId] });
    void queryClient.invalidateQueries({ queryKey: ['usage'] });
    void queryClient.invalidateQueries({ queryKey: ['trash', namespaceId] });
  };

  const newFolder = useMutation({
    mutationFn: (path: string) => createFolder(namespaceId, path, true),
    onSuccess: () => { refresh(); },
    onError: (e: Error) => { toast.error(e); },
  });

  const rename = useMutation({
    mutationFn: ({ from, name }: { from: string; name: string }) =>
      moveNodes(namespaceId, [{ from, to: joinPath(parentPath(from), name) }]),
    onSuccess: () => { refresh(); },
    onError: (e: Error) => { toast.error(e); },
  });

  const move = useMutation({
    mutationFn: ({ paths, targetFolder }: { paths: string[]; targetFolder: string }) =>
      moveNodes(
        namespaceId,
        paths.map((from) => ({ from, to: joinPath(targetFolder, baseName(from)) })),
        true,
      ),
    onSuccess: (_data, variables) => {
      toast.success(`${variables.paths.length}개 항목을 옮겼습니다`);
      clearSelection();
      refresh();
    },
    onError: (e: Error) => { toast.error(e); },
  });

  const copy = useMutation({
    mutationFn: ({ paths, targetFolder }: { paths: string[]; targetFolder: string }) =>
      copyNodes(
        namespaceId,
        paths.map((from) => ({ from, to: joinPath(targetFolder, baseName(from)) })),
      ),
    onSuccess: (result) => {
      toast.success(`${result.copied_items}개 항목을 복사했습니다`);
      refresh();
    },
    onError: (e: Error) => { toast.error(e); },
  });

  /** 삭제는 휴지통으로 보낸다. 토스트에서 바로 되돌릴 수 있게 한다. */
  const remove = useMutation({
    mutationFn: (paths: string[]) => deleteNodes(namespaceId, paths),
    onSuccess: (result, paths) => {
      clearSelection();
      refresh();
      const label = paths.length === 1 ? baseName(paths[0]!) : `${paths.length}개 항목`;
      // 되돌리기는 토스트가 살아 있는 동안만 노출된다. 이후에는 휴지통에서 복원한다.
      useToasts.getState().push(
        `${label}을(를) 휴지통으로 옮겼습니다`,
        'info',
        {
          label: '되돌리기',
          run: () => {
            restoreTrash(namespaceId, result.trash_ids)
              .then(() => { toast.success('되돌렸습니다'); refresh(); })
              .catch((e: Error) => { toast.error(e); });
          },
        },
      );
    },
    onError: (e: Error) => { toast.error(e); },
  });

  /** 파일 1개는 원본 그대로, 그 외(다중 선택·폴더)는 ZIP 으로 내려받는다. */
  const download = async (paths: string[], name: string): Promise<void> => {
    try {
      if (paths.length === 1) {
        window.location.href = await downloadUrl(namespaceId, paths[0]!);
        return;
      }
      window.location.href = await zipUrl(namespaceId, paths, name || '다운로드');
    } catch (e) { toast.error(e); }
  };

  const downloadZip = async (paths: string[], name: string): Promise<void> => {
    try {
      window.location.href = await zipUrl(namespaceId, paths, name);
    } catch (e) { toast.error(e); }
  };

  return { newFolder, rename, move, copy, remove, download, downloadZip, refresh };
}
